import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Button, LinearProgress, Divider } from '@material-ui/core';
import { observer } from 'mobx-react';
import copy from 'copy-to-clipboard';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useFormikContext } from 'formik';

import email from 'core/assets/svg/email.svg';
import cellPhone from 'core/assets/svg/cell-phone.svg';
import copyIcon from 'core/assets/svg/copy.svg';
import infoDarkGreen from 'core/assets/svg/info-dark-green.svg';
import BaseDialog from 'core/components/base-dialog';
import { Label } from 'core/components/label';
import { NotificationCard } from 'core/components/notification-card';
import backIcon from 'core/components/screen-header/go-to.svg';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import CreditCardForm from 'customer/components/policy/payment-tab/credit-card-form/credit-card-form';
import useStyles from './send-credit-card-links-modal.styles';

const SEND_PAYMENT_LINK = gql`
  query getPaymentLink(
    $policyId: String!
    $channel: String!
    $currentPaymentMethod: String!
    $accountEmail: String!
    $accountPhone: String!
  ) {
    link: getPaymentLink(
      policyId: $policyId
      channel: $channel
      currentPaymentMethod: $currentPaymentMethod
      accountEmail: $accountEmail
      accountPhone: $accountPhone
    )
  }
`;

const SendCreditCardLinksModal = ({ open, onClose, onAddCard, defaultPage, isCancelledOrInCancellation }) => {
  const classes = useStyles();
  const { account } = useStore();
  const toast = useToast();
  const { values } = useFormikContext();
  const { isInternalAgent, isAgency } = useSession();
  const {
    policies: {
      policy: { policy }
    }
  } = account;
  const [page, setPage] = useState(defaultPage);

  const [sendPaymentLink, { loading }] = useLazyQuery(SEND_PAYMENT_LINK, {
    variables: {
      policyId: policy.id,
      channel: 'email',
      currentPaymentMethod: values.paymentMethod,
      accountEmail: account.email,
      accountPhone: account.phoneNumber
    }
  });

  const sendEvent = async ({ sendTo }) => {
    try {
      const { data, error } = await sendPaymentLink({ variables: { channel: sendTo } });
      if (data.link && (sendTo === 'sms' || sendTo === 'email')) {
        toast.notify({
          type: 'success',
          message: `A link to add a new card has been sent to ${
            sendTo === 'email' ? account.email : account.phoneNumber
          }`
        });
      } else if (data.link && sendTo === 'copied') {
        toast.notify({
          type: 'success',
          message: 'A link to add a new card has been copied to your clipboard'
        });
        return data.link;
      } else if (error) {
        console.error('Error sending event: ', error);
        toast.notify({
          type: 'error',
          message:
            'Link failed to send. Please try another method. If the problem persists, please follow your designated support process.'
        });
      }
    } catch (error) {
      console.error('Error sending event: ', error);
      toast.notify({
        type: 'error',
        message:
          'Link failed to send. Please try another method. If the problem persists, please follow your designated support process.'
      });
    }
  };

  const externalAgentUserTipText = isCancelledOrInCancellation
    ? 'To process payments with this method, contact support after adding it, since this policy is in cancellation or has been canceled.'
    : 'To set this new card as the default, select it in the billing details section and save your changes. To remove the old card, use the payment methods section below.';
  return (
    <BaseDialog open={open} onClose={onClose} className={classes.container}>
      {/* MARK: Pg 1 Send Links */}
      {page === 1 && !isAgency && (
        <>
          <Label type="titleSecondary" className={classes.title}>
            Send a link to add a new card
          </Label>
          <Label type="darkSmall" className={classes.text}>
            The member will get a secure (pre-authenticated) link, good for one hour, to do this action in their online
            account.
          </Label>

          <Grid container>
            <Grid item xs={12}>
              <Button
                variant="text"
                color="secondary"
                disabled={loading}
                onClick={() => sendEvent({ sendTo: 'email' })}
              >
                <img src={email} alt="Email" className={classes.icon} />
                Send link to email: {account.email}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button variant="text" color="secondary" disabled={loading} onClick={() => sendEvent({ sendTo: 'sms' })}>
                <img src={cellPhone} alt="Cell phone" className={classes.icon} />
                Send link to phone: {account.phoneNumber}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="text"
                color="secondary"
                disabled={loading}
                onClick={async () => {
                  const link = await sendEvent({ sendTo: 'copied' });
                  copy(link);
                }}
              >
                <img src={copyIcon} alt="Copy" className={classes.icon} />
                Copy link to clipboard
              </Button>
            </Grid>
          </Grid>
          <NotificationCard type="light" icon={infoDarkGreen} iconSize="icon16">
            <Label type="notificationText">
              <span style={{ fontWeight: 600 }}>User tip:</span> After the new card has been added, refresh the settings
              tab to see it in the dropdown list.
            </Label>
          </NotificationCard>
          {isInternalAgent && (
            <>
              <Divider className={classes.divider} />
              <Label type="darkSmall" className={classes.actionText}>
                Is this an incidental scenario?{' '}
                <span onClick={() => setPage(2)} className={classes.inlineLink}>
                  Manually add a new card
                </span>
              </Label>
              <NotificationCard type="light" icon={infoDarkGreen} iconSize="icon16" noBackground>
                <Label type="body3" className={classes.calloutText}>
                  A manual action should only be performed when the scenario is incidental (e.g. member is unable to add
                  a card through the link). Adding a card won't trigger an immediate charge.
                </Label>
              </NotificationCard>
            </>
          )}
          {loading && <LinearProgress color="secondary" className={classes.loadingBar} />}
        </>
      )}
      {/* MARK: Pg 2 Manual add card */}
      {(page === 2 || isAgency) && (
        <>
          {/* if the default CC modal page is not 2, we should show the back button */}
          {defaultPage !== 2 && !isAgency && (
            <Button variant="text" color="secondary" onClick={() => setPage(1)} className={classes.backButton}>
              <img src={backIcon} alt="Back" className={classes.backIcon} />
              Back to sending link
            </Button>
          )}
          <Label type="titleSecondary" className={classes.title}>
            Add new card
          </Label>
          <CreditCardForm
            onSubmit={onAddCard}
            buttonText="Add card"
            label="Card Information"
            mode="dark"
            fromModal
            closeModal={onClose}
          >
            <Grid className={classes.notificationContainer}>
              {isInternalAgent && (
                <NotificationCard type="light" icon={infoDarkGreen} iconSize="icon16">
                  <Label type="body3" className={classes.calloutText}>
                    A manual action should only be performed when the scenario is incidental (e.g. member is unable to
                    add card through the link). Adding a card won't trigger an immediate charge.
                  </Label>
                </NotificationCard>
              )}
              <NotificationCard type="light" icon={infoDarkGreen} iconSize="icon16" noBackground>
                <Label type="notificationText">
                  <span style={{ fontWeight: 600 }}>User tip:</span> {externalAgentUserTipText}
                </Label>
              </NotificationCard>
            </Grid>
          </CreditCardForm>
        </>
      )}
    </BaseDialog>
  );
};

SendCreditCardLinksModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddCard: PropTypes.func.isRequired,
  defaultPage: PropTypes.number.isRequired,
  isCancelledOrInCancellation: PropTypes.bool.isRequired
};

export default observer(SendCreditCardLinksModal);
